/**
 * The phone Practice Arena header, outside a session.
 *
 *   Practice Arena                    [History]
 *   🔥 12 DAYS · TODAY 3 SESSIONS · 14 MIN
 *
 * The same shape as the other Mindspace phone headers: the title beside a
 * labelled button (History was an unlabelled clock icon up by Back), then
 * one mono line. That line replaces two chips and a pill - the streak badge
 * beside the title, which wrapped the title onto two lines, and the "Today ·
 * 3 sessions · 9 concepts · 14m" pill under the mode switch.
 */

import { Flame, History } from 'lucide-react';
import { usePracticeStats } from '@/hooks/mindspace/usePracticeHistory';

export function PracticePhoneHeader({
  historyOpen,
  onOpenHistory,
}: {
  historyOpen: boolean;
  onOpenHistory: () => void;
}) {
  return (
    <header className='flex-shrink-0 border-b border-border px-4 pb-3 pt-2'>
      <div className='flex items-center justify-between gap-3'>
        <h2 className='min-w-0 text-xl font-semibold leading-tight text-foreground'>
          Practice Arena
        </h2>
        <button
          type='button'
          onClick={onOpenHistory}
          aria-haspopup='dialog'
          aria-expanded={historyOpen}
          className='flex h-11 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        >
          <History className='h-4 w-4' aria-hidden='true' />
          History
        </button>
      </div>
      <StatsLine />
    </header>
  );
}

/** "🔥 12 days · Today 3 sessions · 14 min" - what there is of it. */
function StatsLine() {
  const { data } = usePracticeStats();
  const streak = data?.streak_current ?? 0;
  const sessions = data?.today_sessions ?? 0;
  const minutes = data?.today_minutes ?? 0;
  const today =
    sessions > 0
      ? `Today ${sessions} session${sessions === 1 ? '' : 's'}${minutes > 0 ? ` · ${minutes} min` : ''}`
      : 'Nothing practiced today';
  return (
    <p
      data-testid='practice-stats'
      className='mt-0.5 flex h-5 items-center gap-1.5 font-mono text-xs font-medium uppercase tracking-[0.1em] text-muted-foreground'
    >
      {streak > 0 && (
        <>
          <Flame className='h-3.5 w-3.5 text-foreground' aria-hidden='true' />
          <span className='text-foreground'>
            {streak} day{streak === 1 ? '' : 's'}
          </span>
          <span aria-hidden='true'>·</span>
        </>
      )}
      <span className='truncate'>{data ? today : ' '}</span>
    </p>
  );
}
